/**
 * Wrapper Credentials page.
 *
 * One row per realm declared in the wrapper spec, plus a "+ Add realm"
 * to register an ad-hoc one. Each row is its own form: pick auth mode,
 * fill mode-specific fields, save. Save encrypts and persists; the row
 * then shows masked values (header_name visible, value hidden).
 *
 * Two test buttons per row:
 *   - Test auth — for OAuth, mints a token at the configured token URL.
 *   - Test tool call — picks one of the spec's tools, runs it end-to-end
 *     against the upstream using the stored creds, returns status + body.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Check, Edit2, KeyRound, Plus, Trash2, Zap } from 'lucide-react';

import {
  ApiError,
  useDeleteWrapperCredential,
  useTestWrapperAuth,
  useTestWrapperTool,
  useUpsertWrapperCredential,
  useWrapperCredentials,
  useWrapperSpecs,
  type WrapperCredentialMode,
  type WrapperCredentialView,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { HelpCallout } from '@/components/console/HelpCallout.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { Required, RequiredLegend } from '@/components/console/Required.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { useUrlParam } from '@/hooks/useUrlState.js';

const MODE_LABELS: Record<WrapperCredentialMode, string> = {
  none: 'None (public API)',
  bearer: 'Static bearer token',
  api_key_header: 'API key in header',
  api_key_query: 'API key in query string',
  basic: 'HTTP basic auth',
  oauth_client_credentials: 'OAuth 2.0 (client credentials)',
  custom_headers: 'Custom headers',
};
const MODES = Object.keys(MODE_LABELS) as WrapperCredentialMode[];

export default function WrapperCredentials(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const credentials = useWrapperCredentials(slug);
  const specs = useWrapperSpecs({ q: slug, limit: 50 });
  const matchedSpec = useMemo(
    () => specs.data?.items.find((s) => s.slug === slug) ?? null,
    [specs.data?.items, slug],
  );

  const declaredRealms = useMemo(() => {
    const spec = matchedSpec?.spec as { auth_realms?: Record<string, unknown> } | undefined;
    return Object.keys(spec?.auth_realms ?? {});
  }, [matchedSpec]);

  // Realms shown = declared union saved.
  const allRealms = useMemo(() => {
    const set = new Set<string>(declaredRealms);
    for (const r of credentials.data?.items ?? []) set.add(r.realm_name);
    return [...set].sort();
  }, [declaredRealms, credentials.data?.items]);

  // Drawer state in URL: ?realm=<name> opens that row's form.
  const [editParam, setEditParam] = useUrlParam('realm', '');

  if (!slug) return <p>Missing slug.</p>;

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title={`Credentials — ${slug}`}
        description="Auth secrets for this REST wrapper's upstream calls. Saved encrypted; never returned over the API."
      >
        <ActionButton
          icon={ArrowRight}
          primary
          onClick={() => navigate(`/catalog/toolkits/${slug}`)}
        >
          Open toolkit
        </ActionButton>
      </PageHeader>

      <HelpCallout
        title="How wrapper credentials work"
        whatHappens={
          <>
            For each <strong>auth realm</strong> your spec uses, save a credential here. The wrapper
            service decrypts on each upstream call and applies the right header / query / OAuth
            flow. Secrets never leave the database in plaintext.
          </>
        }
        youNeed={[
          <>
            For OAuth: <code>token_url</code>, <code>client_id</code>, <code>client_secret</code>{' '}
            (FSS / FASM clients give you these).
          </>,
          <>For API keys: the header or query name + value the upstream expects.</>,
          <>For basic auth: username + password.</>,
        ]}
        afterwards={[
          <>
            Click <strong>Test auth</strong> to verify OAuth tokens mint correctly.
          </>,
          <>
            Click <strong>Test tool call</strong> to actually invoke a tool with stored creds —
            confirms end-to-end before any consumer hits it.
          </>,
        ]}
      />

      {!matchedSpec && (
        <SectionCard
          icon={AlertTriangle}
          title="Spec not found"
          text="No wrapper spec with this slug. Submit one in Register Server first."
        >
          <a href="/build/register?mode=wrapper">Go to Register Server →</a>
        </SectionCard>
      )}

      <AsyncShell
        isLoading={credentials.isLoading}
        isError={credentials.isError}
        isEmpty={false}
        error={credentials.error}
        loadingTitle="Loading credentials"
        errorTitle="Could not load credentials"
        emptyTitle=""
      >
        <SectionCard
          icon={KeyRound}
          title={`Realms (${allRealms.length})`}
          text="One credential per realm. Add new realms ad-hoc; declared realms show first."
        >
          {allRealms.length === 0 && (
            <p className="subtle" style={{ fontSize: 14 }}>
              This spec declares no realms. If your tools need auth, click{' '}
              <strong>Add realm</strong> below.
            </p>
          )}
          <div style={{ display: 'grid', gap: 12 }}>
            {allRealms.map((realm) => {
              const saved = credentials.data?.items.find((c) => c.realm_name === realm) ?? null;
              const isOpen = editParam === realm;
              return (
                <RealmRow
                  key={realm}
                  slug={slug}
                  realmName={realm}
                  saved={saved}
                  open={isOpen}
                  onToggle={() => setEditParam(isOpen ? '' : realm)}
                  spec={matchedSpec?.spec ?? null}
                />
              );
            })}
            <NewRealmRow
              slug={slug}
              onAdded={(name) => setEditParam(name)}
              existingNames={allRealms}
            />
          </div>
        </SectionCard>
      </AsyncShell>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function RealmRow({
  slug,
  realmName,
  saved,
  open,
  onToggle,
  spec,
}: {
  slug: string;
  realmName: string;
  saved: WrapperCredentialView | null;
  open: boolean;
  onToggle: () => void;
  spec: unknown;
}): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 12,
        background: 'rgba(255,255,255,0.5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong style={{ fontSize: 15 }}>
            <code>{realmName}</code>
          </strong>
          <div className="subtle" style={{ fontSize: 13, marginTop: 2 }}>
            {saved
              ? `${MODE_LABELS[saved.mode]} · saved ${formatRelative(saved.rotated_at)}`
              : 'No credential set yet.'}
          </div>
        </div>
        <button type="button" onClick={onToggle} style={btnSecondary}>
          <Edit2 size={13} style={{ marginRight: 6 }} />
          {open ? 'Close' : saved ? 'Edit / rotate' : 'Set up'}
        </button>
      </div>
      {open && <RealmForm slug={slug} realmName={realmName} saved={saved} spec={spec} />}
    </div>
  );
}

function NewRealmRow({
  onAdded,
  existingNames,
}: {
  slug: string;
  onAdded: (name: string) => void;
  existingNames: readonly string[];
}): JSX.Element {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const taken = existingNames.includes(name.trim());
  const valid = /^[a-z0-9_-]+$/.test(name.trim()) && !taken;

  return (
    <div
      style={{
        border: '1px dashed var(--line)',
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <input
        value={name}
        onChange={(e) => {
          setName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''));
          setTouched(true);
        }}
        placeholder="new realm name (e.g. fasm-prod)"
        style={{ ...inputStyle, flex: '1 1 220px' }}
      />
      <button
        type="button"
        onClick={() => valid && onAdded(name.trim())}
        disabled={!valid}
        style={btnSecondary}
      >
        <Plus size={13} style={{ marginRight: 6 }} /> Add realm
      </button>
      {touched && taken && (
        <span style={{ color: '#c0392b', fontSize: 13 }}>realm already exists</span>
      )}
    </div>
  );
}

function RealmForm({
  slug,
  realmName,
  saved,
  spec,
}: {
  slug: string;
  realmName: string;
  saved: WrapperCredentialView | null;
  spec: unknown;
}): JSX.Element {
  const upsert = useUpsertWrapperCredential();
  const remove = useDeleteWrapperCredential();
  const testAuth = useTestWrapperAuth();
  const testTool = useTestWrapperTool();
  const [mode, setMode] = useState<WrapperCredentialMode>(
    saved?.mode ?? 'oauth_client_credentials',
  );
  const [fields, setFields] = useState<Record<string, string | Record<string, string>>>(() =>
    initialFieldsFromSaved(saved),
  );
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setMode(saved?.mode ?? 'oauth_client_credentials');
    setFields(initialFieldsFromSaved(saved));
    setError(null);
    setSavedAt(null);
  }, [saved?.rotated_at, saved?.mode, slug, realmName]);

  async function onSave(): Promise<void> {
    setError(null);
    try {
      await upsert.mutateAsync({
        slug,
        realm_name: realmName,
        mode,
        fields: cleanFields(mode, fields),
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  async function onDelete(): Promise<void> {
    if (!saved) return;
    if (!window.confirm(`Delete credential for realm "${realmName}"?`)) return;
    setError(null);
    try {
      await remove.mutateAsync({ slug, realm_name: realmName });
    } catch (e) {
      setError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
      <RequiredLegend />
      <Field label="Auth mode" required>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as WrapperCredentialMode)}
          style={inputStyle}
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {MODE_LABELS[m]}
            </option>
          ))}
        </select>
      </Field>

      <ModeFields mode={mode} fields={fields} setFields={setFields} saved={saved} />

      {error && (
        <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {error}
        </p>
      )}
      {savedAt && !error && (
        <p style={{ color: '#2f855a', fontSize: 14 }}>
          <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Saved {formatRelative(new Date(savedAt).toISOString())}.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={onSave} disabled={upsert.isPending} style={btnPrimary}>
          <Check size={13} style={{ marginRight: 6 }} />
          {upsert.isPending ? 'Saving…' : saved ? 'Save / rotate' : 'Save'}
        </button>
        {saved && (
          <button
            type="button"
            onClick={async () =>
              testAuth.mutate(
                { slug, realm_name: realmName },
                { onError: (e) => setError(String(e)) },
              )
            }
            disabled={testAuth.isPending}
            style={btnSecondary}
          >
            <Zap size={13} style={{ marginRight: 6 }} />
            {testAuth.isPending ? 'Testing…' : 'Test auth'}
          </button>
        )}
        {saved && (
          <button type="button" onClick={onDelete} disabled={remove.isPending} style={btnDanger}>
            <Trash2 size={13} style={{ marginRight: 6 }} />
            Delete
          </button>
        )}
      </div>

      {testAuth.data && <TestAuthResultPanel result={testAuth.data} />}

      {saved && <TestToolCard slug={slug} spec={spec} testTool={testTool} />}
    </div>
  );
}

/* ── per-mode fields ──────────────────────────────────────────────── */

function ModeFields({
  mode,
  fields,
  setFields,
  saved,
}: {
  mode: WrapperCredentialMode;
  fields: Record<string, string | Record<string, string>>;
  setFields: (next: Record<string, string | Record<string, string>>) => void;
  saved: WrapperCredentialView | null;
}): JSX.Element | null {
  const set = (k: string, v: string | Record<string, string>): void =>
    setFields({ ...fields, [k]: v });

  switch (mode) {
    case 'none':
      return (
        <p className="subtle" style={{ fontSize: 13 }}>
          No fields. The wrapper will hit the upstream without any auth.
        </p>
      );
    case 'bearer':
      return (
        <Field label="Bearer token" required>
          <input
            type="password"
            value={String(fields.token ?? '')}
            onChange={(e) => set('token', e.target.value)}
            placeholder={savedHintToken(saved)}
            style={inputStyle}
            required
          />
        </Field>
      );
    case 'api_key_header':
      return (
        <>
          <Field label="Header name" required>
            <input
              value={String(fields.header_name ?? '')}
              onChange={(e) => set('header_name', e.target.value)}
              placeholder="X-API-Key"
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Header value" required>
            <input
              type="password"
              value={String(fields.value ?? '')}
              onChange={(e) => set('value', e.target.value)}
              placeholder={savedHintValue(saved)}
              style={inputStyle}
              required
            />
          </Field>
        </>
      );
    case 'api_key_query':
      return (
        <>
          <Field label="Query param name" required>
            <input
              value={String(fields.param_name ?? '')}
              onChange={(e) => set('param_name', e.target.value)}
              placeholder="apiKey"
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Param value" required>
            <input
              type="password"
              value={String(fields.value ?? '')}
              onChange={(e) => set('value', e.target.value)}
              placeholder={savedHintValue(saved)}
              style={inputStyle}
              required
            />
          </Field>
        </>
      );
    case 'basic':
      return (
        <>
          <Field label="Username" required>
            <input
              value={String(fields.username ?? '')}
              onChange={(e) => set('username', e.target.value)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Password" required>
            <input
              type="password"
              value={String(fields.password ?? '')}
              onChange={(e) => set('password', e.target.value)}
              placeholder={savedHintValue(saved)}
              style={inputStyle}
              required
            />
          </Field>
        </>
      );
    case 'oauth_client_credentials':
      return (
        <>
          <Field label="Token URL" required>
            <input
              value={String(fields.token_url ?? '')}
              onChange={(e) => set('token_url', e.target.value)}
              placeholder="https://realm.example.com/oauth2/token"
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Client ID" required>
            <input
              value={String(fields.client_id ?? '')}
              onChange={(e) => set('client_id', e.target.value)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Client secret" required>
            <input
              type="password"
              value={String(fields.client_secret ?? '')}
              onChange={(e) => set('client_secret', e.target.value)}
              placeholder={savedHintValue(saved)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Scope (optional)">
            <input
              value={String(fields.scope ?? '')}
              onChange={(e) => set('scope', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </>
      );
    case 'custom_headers': {
      const hdrs = (fields.headers ?? {}) as Record<string, string>;
      const entries = Object.entries(hdrs);
      const updateAt = (idx: number, name: string, value: string): void => {
        const next: Record<string, string> = {};
        entries.forEach(([k, v], i) => {
          if (i === idx) next[name] = value;
          else next[k] = v;
        });
        set('headers', next);
      };
      return (
        <Field label="Headers" required>
          <div style={{ display: 'grid', gap: 6 }}>
            {entries.map(([k, v], idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6 }}>
                <input
                  value={k}
                  onChange={(e) => updateAt(idx, e.target.value, v)}
                  placeholder="header name"
                  style={{ ...inputStyle, flex: '1 1 160px' }}
                />
                <input
                  type="password"
                  value={v}
                  onChange={(e) => updateAt(idx, k, e.target.value)}
                  placeholder="value"
                  style={{ ...inputStyle, flex: '2 1 240px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...hdrs };
                    delete next[k];
                    set('headers', next);
                  }}
                  style={btnSecondary}
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set('headers', { ...hdrs, '': '' })}
              style={{ ...btnSecondary, alignSelf: 'flex-start' }}
            >
              <Plus size={13} style={{ marginRight: 6 }} /> Add header
            </button>
          </div>
        </Field>
      );
    }
  }
}

/* ── Test result panels ───────────────────────────────────────────── */

function TestAuthResultPanel({
  result,
}: {
  result: { ok: boolean; status?: number; error_body?: string; error?: string; message?: string };
}): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        background: result.ok ? 'rgba(47, 133, 90, 0.06)' : 'rgba(192, 57, 43, 0.06)',
        borderRadius: 8,
        padding: 10,
        fontSize: 13,
      }}
    >
      <strong style={{ color: result.ok ? '#2f855a' : '#c0392b' }}>
        {result.ok ? '✓ Auth probe succeeded' : '✗ Auth probe failed'}
      </strong>
      {result.status !== undefined && (
        <div style={{ marginTop: 4 }}>
          HTTP <code>{result.status}</code>
        </div>
      )}
      {result.message && <div style={{ marginTop: 4 }}>{result.message}</div>}
      {result.error_body && (
        <pre
          style={{
            marginTop: 6,
            padding: 8,
            background: 'rgba(0,0,0,0.05)',
            borderRadius: 6,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
          }}
        >
          {result.error_body}
        </pre>
      )}
      {result.error && <div style={{ marginTop: 4 }}>{result.error}</div>}
    </div>
  );
}

function TestToolCard({
  slug,
  spec,
  testTool,
}: {
  slug: string;
  spec: unknown;
  testTool: ReturnType<typeof useTestWrapperTool>;
}): JSX.Element {
  const tools = useMemo(() => {
    const s = spec as { tools?: { name: string }[] } | null;
    return s?.tools ?? [];
  }, [spec]);
  const [toolName, setToolName] = useState('');
  const [argsText, setArgsText] = useState('{}');
  useEffect(() => {
    if (!toolName && tools[0]) setToolName(tools[0].name);
  }, [tools, toolName]);

  function onRun(): void {
    let args: Record<string, unknown> = {};
    try {
      args = JSON.parse(argsText) as Record<string, unknown>;
    } catch {
      args = {};
    }
    if (toolName) testTool.mutate({ slug, tool_name: toolName, args });
  }

  return (
    <div
      style={{
        marginTop: 8,
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 12,
      }}
    >
      <strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>Test tool call</strong>
      <div style={{ display: 'grid', gap: 8 }}>
        <Field label="Tool" required>
          <select value={toolName} onChange={(e) => setToolName(e.target.value)} style={inputStyle}>
            {tools.length === 0 && <option value="">(no tools in this spec)</option>}
            {tools.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Arguments (JSON)">
          <textarea
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            rows={4}
            style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
          />
        </Field>
        <button
          type="button"
          onClick={onRun}
          disabled={testTool.isPending || !toolName}
          style={btnPrimary}
        >
          <Zap size={13} style={{ marginRight: 6 }} />
          {testTool.isPending ? 'Running…' : 'Run'}
        </button>
        {testTool.data && (
          <div
            style={{
              border: '1px solid var(--line)',
              background: testTool.data.ok ? 'rgba(47, 133, 90, 0.06)' : 'rgba(192, 57, 43, 0.06)',
              borderRadius: 8,
              padding: 10,
              fontSize: 13,
            }}
          >
            <strong style={{ color: testTool.data.ok ? '#2f855a' : '#c0392b' }}>
              {testTool.data.ok ? '✓ Tool call succeeded' : '✗ Tool call failed'}
            </strong>
            {testTool.data.status !== undefined && (
              <div>
                HTTP <code>{testTool.data.status}</code>
                {testTool.data.latency_ms !== undefined && (
                  <span className="subtle"> · {testTool.data.latency_ms}ms</span>
                )}
              </div>
            )}
            {testTool.data.body && (
              <pre
                style={{
                  marginTop: 6,
                  padding: 8,
                  background: 'rgba(0,0,0,0.05)',
                  borderRadius: 6,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {testTool.data.body}
              </pre>
            )}
            {testTool.data.error && <div style={{ marginTop: 4 }}>{testTool.data.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── helpers + styles ─────────────────────────────────────────────── */

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
        {label}
        {required && <Required />}
      </span>
      {children}
    </label>
  );
}

function initialFieldsFromSaved(
  saved: WrapperCredentialView | null,
): Record<string, string | Record<string, string>> {
  if (!saved) return {};
  const f = saved.fields;
  const out: Record<string, string | Record<string, string>> = {};
  for (const [k, v] of Object.entries(f)) {
    if (typeof v === 'string') out[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out.headers = Object.fromEntries((v as string[]).map((n) => [n, '']));
    } else if (v && typeof v === 'object') {
      out[k] = v as Record<string, string>;
    }
  }
  return out;
}

function cleanFields(
  mode: WrapperCredentialMode,
  fields: Record<string, string | Record<string, string>>,
): Record<string, unknown> {
  const f: Record<string, unknown> = { ...fields };
  if (mode === 'custom_headers') {
    const h = (f.headers ?? {}) as Record<string, string>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(h)) {
      if (k.trim() && v) out[k.trim()] = v;
    }
    f.headers = out;
  }
  return f;
}

function savedHintToken(saved: WrapperCredentialView | null): string {
  return saved?.fields.has_token ? '••••••• (already saved — type to replace)' : '';
}
function savedHintValue(saved: WrapperCredentialView | null): string {
  const has =
    Boolean(saved?.fields.has_value) ||
    Boolean(saved?.fields.has_password) ||
    Boolean(saved?.fields.has_client_secret);
  return has ? '••••••• (already saved — type to replace)' : '';
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const delta = Date.now() - d;
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.round(delta / 3_600_000)}h ago`;
  return `${Math.round(delta / 86_400_000)}d ago`;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid rgba(140, 21, 21, 0.3)',
  background: 'linear-gradient(135deg, #8c1515, #b83a24)',
  color: '#fff8ec',
  fontWeight: 650,
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
};
const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--line-strong)',
  background: 'rgba(255,255,255,0.85)',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};
const btnDanger: React.CSSProperties = {
  ...btnSecondary,
  border: '1px solid #d97766',
  color: '#c0392b',
};
